import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './SociosList.css';
import SocioForm from './SocioForm';
import SocioDetalle from './SocioDetalle';
import DashboardButton from './ui/DashboardButton';
import { apiFetch } from '../../../services/dashboardApi';
import { FaSearch } from 'react-icons/fa';

function SociosList({ token, isAdmin, user, canAccess }) {
    const navigate = useNavigate();
    const [socios, setSocios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [nextPage, setNextPage] = useState(null);
    const [prevPage, setPrevPage] = useState(null);
    const [pageSize, setPageSize] = useState(5);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [selectedSocio, setSelectedSocio] = useState(null);
    const [viewDetalle, setViewDetalle] = useState(false);
    const [solicitudLoading, setSolicitudLoading] = useState(false);
    const [solicitudMessage, setSolicitudMessage] = useState('');
    const [solicitudMessageType, setSolicitudMessageType] = useState('');
    const didMountSearchRef = useRef(false);
    const userRole = (user?.rol_principal || user?.rol || user?.role || '').toUpperCase();
    const canRequestRegistroSocio = !isAdmin && ['SOCIO', 'REVISOR'].includes(userRole);

    const fetchSocios = useCallback(async (page = 1, search = appliedSearchTerm) => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: String(page),
                page_size: String(pageSize),
            });

            if (!isAdmin && user?.email) {
                params.set('search', user.email);
            } else if (search?.trim()) {
                params.set('search', search.trim());
            }

            const response = await apiFetch(`http://localhost:8000/api/socios/?${params.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            let lista = data.results || [];
            if (!isAdmin && user?.email) {
                lista = lista.filter((s) => s.email === user.email);
            }
            setSocios(lista);
            setTotalCount(data.count || lista.length);
            setNextPage(data.next || null);
            setPrevPage(data.previous || null);
            setCurrentPage(page);
            setError(null);
        } catch (err) {
            setError('Error al cargar socios: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, [token, isAdmin, user?.email, pageSize, appliedSearchTerm]);

    useEffect(() => {
        if (canAccess) {
            fetchSocios(currentPage, appliedSearchTerm);
        } else {
            setLoading(false);
        }
    }, [canAccess, fetchSocios, currentPage, appliedSearchTerm]);

    useEffect(() => {
        if (!isAdmin) return;
        if (!didMountSearchRef.current) {
            didMountSearchRef.current = true;
            return;
        }

        const timer = setTimeout(() => {
            setAppliedSearchTerm(searchTerm.trim());
            setCurrentPage(1);
        }, 350);

        return () => clearTimeout(timer);
    }, [searchTerm, isAdmin]);

    const handleApplySearch = () => {
        const term = searchTerm.trim();
        setAppliedSearchTerm(term);
        setCurrentPage(1);
        fetchSocios(1, term);
    };

    const handlePageSizeChange = (event) => {
        setPageSize(Number(event.target.value));
        setCurrentPage(1);
    };

    const handleCrearSocio = () => {
        navigate('/dashboard/registro-socios');
    };

    const handleEditarSocio = (socio) => {
        setSelectedSocio(socio);
        setShowForm(true);
        setViewDetalle(false);
    };

    const handleVerDetalle = (socio) => {
        setSelectedSocio(socio);
        setViewDetalle(true);
        setShowForm(false);
    };

    const handleCancelar = () => {
        setShowForm(false);
        setViewDetalle(false);
        setSelectedSocio(null);
        fetchSocios(currentPage, appliedSearchTerm);
    };

    useEffect(() => {
        // Flujo para no admins: si tiene socio, mostrar detalle
        if (!isAdmin && !loading) {
            if (socios.length > 0) {
                setSelectedSocio(socios[0]);
                setViewDetalle(true);
                setShowForm(false);
            }
        }
    }, [isAdmin, loading, socios]);

    const handleSolicitudRegistroSocio = async () => {
        setSolicitudMessage('');
        setSolicitudMessageType('');
        setSolicitudLoading(true);

        const nombre = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.username || 'Usuario';
        const correo = user?.email || '';
        const telefono = user?.telefono || user?.phone || '';

        if (!correo) {
            setSolicitudMessage('No se encontró un correo en tu cuenta para enviar la solicitud.');
            setSolicitudMessageType('error');
            setSolicitudLoading(false);
            return;
        }

        try {
            const response = await apiFetch('http://localhost:8000/api/contacto/solicitud-registro-socio/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    nombre,
                    correo,
                    telefono
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || 'No se pudo enviar la solicitud');
            }

            setSolicitudMessage(data?.message || 'Solicitud enviada exitosamente.');
            setSolicitudMessageType('success');
        } catch (err) {
            setSolicitudMessage(err.message || 'Error al enviar la solicitud.');
            setSolicitudMessageType('error');
        } finally {
            setSolicitudLoading(false);
        }
    };

    const handleEliminar = async (socioId) => {
        if (!window.confirm('¿Está seguro de eliminar este socio?')) return;

        try {
            const response = await apiFetch(`http://localhost:8000/api/socios/${socioId}/`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                alert('Socio eliminado exitosamente');
                if (socios.length === 1 && currentPage > 1) {
                    setCurrentPage((prev) => prev - 1);
                } else {
                    fetchSocios(currentPage, appliedSearchTerm);
                }
            } else {
                alert('Error al eliminar socio');
            }
        } catch (err) {
            alert('Error: ' + err.message);
        }
    };

    const handleGenerarCredencial = async (socioId, socioNombre) => {
        if (!window.confirm(`¿Generar credencial PDF para ${socioNombre}?`)) return;

        try {
            setLoading(true);
            const response = await apiFetch(`http://localhost:8000/api/socios/${socioId}/generar-credencial/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (response.ok) {
                // Crear un modal para mostrar el QR
                const modal = document.createElement('div');
                modal.style.cssText = `
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(0,0,0,0.8);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                `;

                modal.innerHTML = `
                    <div class="modal-content">
                        <h2>Credencial Generada</h2>
                        <p>Escanea este código QR para acceder a la credencial de <strong>${data.socio}</strong></p>
                        <img src="${data.qr_url}" alt="QR Code" class="modal-qr" />
                        <div class="modal-actions">
                            <button id="downloadQR" class="download">Descargar QR</button>
                            <button id="viewPDF" class="view">Ver PDF</button>
                            <button id="closeModal" class="modal-close">Cerrar</button>
                        </div>
                    </div>
                `;

                modal.className = 'modal-overlay';
                document.body.appendChild(modal);

                // Event listeners
                document.getElementById('closeModal').onclick = () => {
                    document.body.removeChild(modal);
                };

                document.getElementById('downloadQR').onclick = () => {
                    const link = document.createElement('a');
                    link.href = data.qr_url;
                    link.download = data.qr_filename;
                    link.click();
                };

                document.getElementById('viewPDF').onclick = () => {
                    window.open(data.pdf_url, '_blank');
                };

                // Cerrar al hacer clic fuera del contenido
                modal.onclick = (e) => {
                    if (e.target === modal) {
                        document.body.removeChild(modal);
                    }
                };
            } else {
                alert(data.detail || 'Error al generar credencial');
            }
        } catch (err) {
            alert('Error: ' + err.message);
        } finally {
            setLoading(false);
        }
    };


    if (viewDetalle && selectedSocio) {
        return (
            <SocioDetalle
                socio={selectedSocio}
                token={token}
                onVolver={handleCancelar}
                onEditar={() => handleEditarSocio(selectedSocio)}
            />
        );
    }

    if (showForm) {
        return (
            <SocioForm
                socio={selectedSocio}
                token={token}
                onCancelar={handleCancelar}
                onSuccess={(nuevo) => {
                    // tras crear/editar recargar y mostrar detalle
                    fetchSocios(currentPage, appliedSearchTerm);
                    const socioMostrado = nuevo || selectedSocio;
                    if (socioMostrado) {
                        setSelectedSocio(socioMostrado);
                        setShowForm(false);
                        setViewDetalle(true);
                    }
                }}
                defaultEmail={user?.email}
            />
        );
    }

    if (!canAccess) {
        return <p className="error-text">Acceso restringido: requiere rol SOCIO/REVISOR y estar marcado como Staff.</p>;
    }

    if (!loading && canRequestRegistroSocio && socios.length === 0) {
        return (
            <div className="solicitud-registro-socio-panel">
                <h3>Solicitud de Registro de Socio</h3>
                <p>
                    Aun no tienes un perfil de socio registrado. Envía la solicitud para que administración
                    habilite tu registro desde la bandeja de contacto.
                </p>
                <DashboardButton
                    onClick={handleSolicitudRegistroSocio}
                    variant="success"
                    disabled={solicitudLoading}
                >
                    {solicitudLoading ? 'Enviando...' : 'Solicitud Registro Socio'}
                </DashboardButton>
                {solicitudMessage && (
                    <p className={solicitudMessageType === 'success' ? 'success-text' : 'error-text'}>
                        {solicitudMessage}
                    </p>
                )}
            </div>
        );
    }

    return (
        <div>
            <h3>Gestión de Socios</h3>
            {isAdmin && (
                <DashboardButton onClick={handleCrearSocio} variant="success" className="create-btn">
                    Crear Nuevo Socio
                </DashboardButton>
            )}
            {isAdmin && (
                <div className="socios-toolbar">
                    <div className="socios-search">
                        <input
                            type="text"
                            placeholder="Buscar por nombre o apellido..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleApplySearch();
                                }
                            }}
                        />
                        <button className="socios-search-btn" onClick={handleApplySearch}>
                            <FaSearch />
                        </button>
                    </div>
                    <div className="socios-page-size">
                        <label htmlFor="socios-page-size">Mostrar:</label>
                        <select
                            id="socios-page-size"
                            value={pageSize}
                            onChange={handlePageSizeChange}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                        </select>
                    </div>
                </div>
            )}

            {loading && <p>Cargando socios...</p>}
            {error && <p className="error-text">{error}</p>}

            {!loading && socios.length === 0 && (
                <p>No hay socios registrados.</p>
            )}

            {isAdmin && !loading && socios.length > 0 && (
                <>
                    <table className="table-full">
                        <thead>
                            <tr className="table-head">
                                <th className="th">RegABIS</th>
                                <th className="th">Apellidos</th>
                                <th className="th">Nombres</th>
                                <th className="th">CI</th>
                                <th className="th">Email</th>
                                <th className="th">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {socios.map(socio => (
                                <tr key={socio.id}>
                                    <td className="td">{socio.reg_abis || 'N/A'}</td>
                                    <td className="td">{socio.apellidos || 'N/A'}</td>
                                    <td className="td">{socio.nombres || 'N/A'}</td>
                                    <td className="td">{socio.ci || 'N/A'}</td>
                                    <td className="td">{socio.email || 'N/A'}</td>
                                    <td className="td">
                                        <DashboardButton onClick={() => handleVerDetalle(socio)} variant="view" size="sm">
                                            Ver Detalle
                                        </DashboardButton>
                                        <DashboardButton onClick={() => handleEditarSocio(socio)} variant="warning" size="sm">
                                            Editar
                                        </DashboardButton>
                                        <DashboardButton
                                            onClick={() => handleGenerarCredencial(socio.id, `${socio.apellidos} ${socio.nombres}`)}
                                            variant="success"
                                            size="sm"
                                            disabled={loading}
                                        >
                                            {loading ? 'Generando...' : 'Credencial'}
                                        </DashboardButton>
                                        <DashboardButton onClick={() => handleEliminar(socio.id)} variant="danger" size="sm">
                                            Eliminar
                                        </DashboardButton>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="pagination-controls">
                        <DashboardButton
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            variant="view"
                            disabled={!prevPage || currentPage === 1}
                        >
                            ← Anterior
                        </DashboardButton>
                        <span className="pagination-text">
                            Página {currentPage} de {Math.max(1, Math.ceil(totalCount / pageSize))} ({socios.length} de {totalCount})
                        </span>
                        <DashboardButton
                            onClick={() => setCurrentPage((prev) => prev + 1)}
                            variant="view"
                            disabled={!nextPage}
                        >
                            Siguiente →
                        </DashboardButton>
                    </div>
                </>
            )}
        </div>
    );
}

export default SociosList;
